import React from 'react';
import Marquee from 'react-fast-marquee';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Container,
  useTheme,
} from '@mui/material';

const Testimonials = () => {
  const theme = useTheme(); // For accessing theme colors and spacing

  const testimonials = [
    {
      quote: "Excellent service for both laptop repair and software support. The software installation was done swiftly. I'm thoroughly impressed!",
      author: "Swaran",
      role: "Client"
    },
    {
      quote: "Top-notch service. My laptop was fixed in no time and works better than ever!",
      author: "Santhosh",
      role: "Client"
    },
    {
      quote: "Highly recommend their services! Quick diagnosis and efficient repair. My go-to for all tech issues.",
      author: "Priya",
      role: "Client"
    },
    {
      quote: "Fantastic support! They resolved my software conflicts seamlessly and gave great advice for future maintenance.",
      author: "Arjun",
      role: "Client"
    },
    {
      quote: "Professional and very helpful. My computer was running slow, and now it's super fast thanks to their optimization service.",
      author: "Deepa",
      role: "Client"
    },
    {
      quote: "Absolutely brilliant! From hardware repair to software upgrades, everything was handled with expertise.",
      author: "Vikram",
      role: "Client"
    }
  ];

  return (
    <Box
      id="testimonials"
      sx={{
        py: 8,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            component="h2"
            fontWeight="fontWeightExtraBold"
            sx={{
              mb: 2,
              color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[900],
              pb: 1, // padding bottom to create space for the border
              borderBottom: `4px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main}`,
              display: 'inline-block'
            }}
          >
            Client Feedbacks
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[600],
            }}
          >
            What Our Valued Clients Say About Our Services!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[700],
            }}
          >
            We pride ourselves on delivering top-tier laptop repair, software support, and IT solutions.
            Here’s what some of our satisfied customers have to say about their experience with our dedicated team.
          </Typography>
        </Box>

        <Marquee
          gradient={false}
          speed={50}
          pauseOnHover={true}
          style={{ paddingBlock: theme.spacing(2) }}
        >
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              sx={{
                width: '320px', // Fixed width for each card
                height: '200PX',
                mx: 2, // Margin between cards
                boxShadow: 3, // Initial shadow
                borderRadius: theme.shape.borderRadius,
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 8, // Deeper shadow on hover
                },
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.common.white,
                border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200]}`,
                // Animation for staggered fade-in
                opacity: 0,
                transform: 'translateY(20px)',
                animation: `fadeIn 0.7s ease-out forwards ${index * 0.1}s`,
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="body1"
                  component="p"
                  fontStyle="italic"
                  sx={{
                    mb: 3,
                    color: theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[800],
                    position: 'relative',
                    '&::before': {
                      content: '"\\201C"', // Left double quotation mark
                      position: 'absolute',
                      top: theme.spacing(-2),
                      left: theme.spacing(-2),
                      fontSize: '3rem',
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                      opacity: 0.75,
                    },
                    '&::after': {
                      content: '"\\201D"', // Right double quotation mark
                      position: 'absolute',
                      bottom: theme.spacing(-2),
                      right: theme.spacing(-2),
                      fontSize: '3rem',
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                      opacity: 0.75,
                    },
                  }}
                >
                  "{testimonial.quote}"
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      mr: 2,
                      width: 48,
                      height: 48,
                      fontSize: '1.5rem',
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    {testimonial.author.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="fontWeightMedium"
                      sx={{
                        color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[900],
                      }}
                    >
                      {testimonial.author}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        color: theme.palette.primary.main,
                      }}
                    >
                      {testimonial.role}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Marquee>
      </Container>
    </Box>
  );
};

export default Testimonials;